import { OptionsType, togaApiRequest } from "../../../../api/toga";
import { Invoice, OrderDetails } from "../../view/OrderView/types";
import {
	FetchedApprovalData,
	CurrentStageBeforeFormatting,
	CurrentStage,
	ApprovalDecision,
	ApprovalDataWithAllowApproveOrDeny,
} from "../../types";
export interface Field {
	[key: string]: string | string[] | Field;
}
import { UpdateApprovalWorkflowPayload } from "../../../../globalTypes";

export interface ExtendedOptionsType extends Omit<OptionsType, "fields"> {
	depth?: number;
	fields?: (
		| string
		| Field
		| { location: string[]; shipToAddress: string[] }
	)[];
	where?: any;
	join?: any;
	ojoin?: any;
	sort?: any;
	recordsPerPage?: number;
	page?: number;
	[key: string]: any;
}

type ShipmentDetailsOutput = {
	salesOrder: OrderDetails | null;
	invoices: Invoice[];
};

type ApprovalStageAllowedRole = {
	uuid: string;
	approvalTemplateStage: {
		uuid: string;
		step: number;
		name: "Pending Initial Approval";
		approvalTemplateStageAllowedPersonas: [];
		approvalTemplateStageAllowedUsers: [];
	};
	role: {
		uuid: string;
		name: string;
		description: string;
	};
	_updated: string | null;
	_created: string | null;
};

export async function getShipmentDetails(trackingNumber: string) {
	const storedHostName = localStorage.getItem("hostName");

	let output: ShipmentDetailsOutput = {
		salesOrder: null,
		invoices: [],
	};
	/* -------------------------------------- Shipment Details ------------------------------------- */
	let options: ExtendedOptionsType = {
		depth: 5,
		fields: [
			"number",
			"Items.partNumber",
			"Items.skuNumber",
			"Items.upcCode",
			"Items.description",
			"ItemFulfillmentItems.quantity",
			"Units.serialNumber",
		],
		join: [
			{
				ItemFulfillmentPackages: {
					"ItemFulfillmentPackages.trackingNumberId":
						"TrackingNumbers.id",
				},
			},
			{
				ItemFulfillmentItems: {
					"ItemFulfillmentItems.itemFulfillmentId":
						"ItemFulfillmentPackages.itemFulfillmentId",
				},
			},
			{
				ItemFulfillmentItemUnits: {
					"ItemFulfillmentItemUnits.itemFulfillmentItemId":
						"ItemFulfillmentItems.id",
				},
			},
			{
				Units: {
					"Units.id": "ItemFulfillmentItemUnits.unitId",
				},
			},
			{
				Items: {
					"Items.id": "Units.itemId",
				},
			},
		],
		where: {
			and: [{ number: { "=": trackingNumber } }],
		},
	};
	let response = await togaApiRequest(
		"GET",
		"/tracking-numbers",
		null,
		options,
	);
	if (!response.isSuccess) {
		return;
	}
	output = response;
	return output;
}

// initial approvals call
export async function getApprovals(soUuid: string) {
	const options: ExtendedOptionsType = {
		depth: 3,
		fields: [
			"uuid",
			"approvalTemplate.uuid",
			"primaryKeyId",
			"SalesOrders.uuid",
			"SalesOrders.number",
			"SalesOrders.dateOrder",
			"SalesOrders.approvalUserId",
			"ApprovalTemplates.uuid",
			"ApprovalTemplates.recordId",
			"ApprovalTemplates.name",
			"ApprovalTemplates.isActive",
			"ApprovalTemplateStages.uuid",
			"ApprovalTemplateStages.step",
			"ApprovalTemplateStages.name",
			"ApprovalTemplateStageAllowedRoles.uuid",
			"ApprovalTemplateStageAllowedRoles.roleId",
			"ApprovalTemplateStageAllowedRoles.approvalTemplateStageId",
			"Roles.uuid",
			"Roles.name",
			"Users.uuid",
			"Users.firstName",
			"Users.lastName",
			"Users.email",
			"Users.isActive",
			"Users.c_isVip",
			"Users.c_payGroupNbr",
		],

		where: {
			and: [
				{ "SalesOrders.uuid": { "=": soUuid } },
				{ "ApprovalTemplates.isActive": { "=": 1 } },
				{ "ApprovalTemplates.recordId": { "=": 14 } },
			],
		},
	};

	const response = togaApiRequest("GET", "/approvals", null, options);
	return response;
}

export async function getApprovalsBySalesOrderUuid(soUuid: string) {
	const options: ExtendedOptionsType = {
		depth: 5,
		where: {
			and: [
				{ "SalesOrders.uuid": { "=": soUuid } },
				{ recordId: { "=": "14" } },
			],
		},
		join: [
			{
				SalesOrders: {
					"SalesOrders.id": "Approvals.primaryKeyId",
				},
			},
		],
	};
	return togaApiRequest("GET", "/approvals", null, options);
}

// helper API call for all 4 approval decisions modal
// will give all the stages and the decisions for each stage
// will return every stage out there, and decision for each of those stages
export async function getCurrentApprovalStage(
	soUuid: string,
	userUuid: string,
) {
	const options: ExtendedOptionsType = {
		fields: [
			"Approvals.uuid",
			"ApprovalTemplateStages.step",
			"ApprovalTemplateStages.name",
			"ApprovalTemplateStages.uuid",
			"ApprovalDecisions.uuid",
			"ApprovalDecisions.dtDecision",
			"ApprovalDecisions.isApproved",
			"ApprovalDecisions.note",
			"AssignedTo.uuid",
			"AssignedTo._name",
			"AssignedTo.email",
			"DecidedBy.uuid",
			"DecidedBy._name",
			"DecidedBy.email",
			"ApprovalDecisionTypes.name",
			"ApprovalDecisionTypes.uuid",
		],
		sort: ["+ApprovalTemplateStages.step"],
		depth: 5,
		where: {
			and: [{ "SalesOrders.uuid": { "=": soUuid } }],
		},
		join: [
			{
				Approvals: {
					"SalesOrders.Id": "Approvals.primaryKeyId",
					"Approvals.recordId": 14,
				},
			},
			{
				ApprovalTemplateStages: {
					"ApprovalTemplateStages.approvalTemplateId":
						"Approvals.approvalTemplateId",
				},
			},
		],
		ojoin: [
			{
				ApprovalDecisions: {
					"ApprovalDecisions.approvalId": "Approvals.id",
					"ApprovalDecisions.approvalTemplateStageId":
						"ApprovalTemplateStages.id",
				},
			},
			{
				"Users@DecidedBy": {
					"DecidedBy.id": "ApprovalDecisions.decidedByUserId",
				},
			},
			{
				ApprovalDecisionTypes: {
					"ApprovalDecisionTypes.id":
						"ApprovalDecisions.approvalDecisionTypeId",
				},
			},
			{
				"Users@AssignedTo": {
					"AssignedTo.id": "ApprovalDecisions.assignedToUserId",
				},
			},
		],
	};

	const initialResponse = await togaApiRequest(
		"GET",
		"/sales-orders",
		null,
		options,
	);

	const salesOrderApprovalStages = initialResponse.data.salesOrders;
	const salesOrderApprovalStagesWithAllowApproveDeny = (await Promise.all(
		salesOrderApprovalStages.map(async (stage: FetchedApprovalData) => {
			const options: ExtendedOptionsType = {
				fields: [
					`_allowApproveDeny(salesOrderUuid:${soUuid};userUuid:${userUuid})`,
				],
			};
			const allowApproveDenyResponseForEachStage = await togaApiRequest(
				"GET",
				`/approval-template-stages/${stage.ApprovalTemplateStages.uuid}`,
				null,
				options,
			);
			const isPreApproved = stage.ApprovalDecisionTypes.name
				? true
				: false;

			const allowedRolesFetchedResponse =
				await getApprovalTemplateAllowedRoles(stage);

			const allowedRoles =
				allowedRolesFetchedResponse.data.approvalTemplateStageAllowedRoles?.map(
					(role: ApprovalStageAllowedRole) => role.role.name,
				);

			const allowApproveDenyBoolean =
				allowApproveDenyResponseForEachStage.data.approvalTemplateStages
					._allowApproveDeny;

			return {
				...stage,
				ApprovalTemplateStages: {
					...stage.ApprovalTemplateStages,
					allowApproveDeny: allowApproveDenyBoolean,
				},
				allowedRoles: allowedRoles,
				isPreApproved: isPreApproved,
				ApprovalDecisionTypes: stage.ApprovalDecisionTypes,
			};
		}),
	)) as ApprovalDataWithAllowApproveOrDeny[];

	const findCurrentStage = async (
		stages: ApprovalDataWithAllowApproveOrDeny[],
	) => {
		// First, try to find a stage with null approval
		for (const stage of stages) {
			if (stage.ApprovalDecisions?.isApproved === null) {
				return stage;
			}
		}

		// If no null approval found, return the stage with the highest step number
		if (stages.length > 0) {
			return stages.reduce((highest, current) => {
				return current.ApprovalTemplateStages.step >
					highest.ApprovalTemplateStages.step
					? current
					: highest;
			});
		}

		return null;
	};

	const currentStage = await findCurrentStage(
		salesOrderApprovalStagesWithAllowApproveDeny,
	);

	const formattedResponseWithCurrentStage = {
		currentStage: currentStage,
		stages: salesOrderApprovalStagesWithAllowApproveDeny,
	};

	return formattedResponseWithCurrentStage as ApprovalDecision;
}

export async function getApprovalTemplateAllowedRoles(
	currentStage: CurrentStageBeforeFormatting,
) {
	const options: ExtendedOptionsType = {
		depth: 7,
		where: {
			and: [
				{
					"ApprovalTemplateStages.uuid": {
						"=": currentStage.ApprovalTemplateStages.uuid,
					},
				},
			],
		},
		join: [
			{
				ApprovalTemplateStages: {
					"ApprovalTemplateStages.id":
						"ApprovalTemplateStageAllowedRoles.approvalTemplateStageId",
				},
			},
		],
	};

	return togaApiRequest(
		"GET",
		"/approval-template-stage-allowed-roles",
		null,
		options,
	);
}

export async function makeApprovalDecision(
	currentStage: CurrentStage,
	userUuid: string,
	decision: boolean,
	note?: string,
) {
	try {
		const response = await togaApiRequest("POST", `/approval-decisions`, {
			dtDecision: "NOW()",
			isApproved: decision,
			note: note?.trim() || null,
			approval: { uuid: currentStage.Approvals.uuid },
			approvalTemplateStage: {
				uuid: currentStage.ApprovalTemplateStages.uuid,
			},
			assignedToUserId: {
				uuid: userUuid,
			},
			decidedByUserId: {
				uuid: userUuid,
			},
		});

		if (!response?.isSuccess) {
			throw new Error(
				response?.message || "Failed to make approval decision",
			);
		}

		return response;
	} catch (error) {
		console.error("Error making approval decision:", error);
		throw error;
	}
}

export async function updateApprovalDecision(
	currentStage: CurrentStage,
	userUuid: string,
	decision: boolean,
	note?: string,
) {
	try {
		const response = await togaApiRequest(
			"PUT",
			`/approval-decisions/${currentStage.ApprovalDecisions.uuid}`,
			{
				dtDecision: "NOW()",
				isApproved: decision,
				note: note?.trim() || null,
				approval: { uuid: currentStage.Approvals.uuid },
				approvalTemplateStage: {
					uuid: currentStage.ApprovalTemplateStages.uuid,
				},
				assignedToUserId: {
					uuid: userUuid,
				},
				decidedByUserId: {
					uuid: userUuid,
				},
			},
		);

		if (!response?.isSuccess) {
			throw new Error(
				response?.message || "Failed to update approval decision",
			);
		}

		return response;
	} catch (error) {
		console.error("Error updating approval decision:", error);
		throw error;
	}
}

export async function assignApprovalDecisionStage(
	currentStage: CurrentStage,
	userUuid: string,
) {
	return togaApiRequest("POST", `/approval-decisions`, {
		approval: { uuid: currentStage.Approvals.uuid },
		approvalTemplateStage: {
			uuid: currentStage.ApprovalTemplateStages.uuid,
		},
		assignedToUserId: {
			uuid: userUuid,
		},
	});
}

export async function updateAssigneeApprovalDecisionStage(
	currentStage: CurrentStage,
	userUuid: string,
) {
	return togaApiRequest(
		"PUT",
		`/approval-decisions/${currentStage.ApprovalDecisions.uuid}`,
		{
			approval: { uuid: currentStage.Approvals.uuid },
			approvalTemplateStage: {
				uuid: currentStage.ApprovalTemplateStages.uuid,
			},
			assignedToUserId: {
				uuid: userUuid,
			},
		},
	);
}

export async function updateApprovalWorkflow(
	payload: UpdateApprovalWorkflowPayload,
) {
	try {
		const response = await togaApiRequest(
			payload.method,
			payload.route,
			payload.payload,
		);

		if (!response?.isSuccess) {
			throw new Error(
				response?.message || "Failed to update approval decision",
			);
		}

		return response;
	} catch (error) {
		console.error("Error updating approval decision:", error);
		throw error;
	}
}
